import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import Collapse from '@mui/material/Collapse';
import SearchIcon from '@mui/icons-material/Search';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PropTypes from 'prop-types';

const WalletsSidebar = ({ sources = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);

  // Get source_id from URL query parameters
  const getSelectedSourceId = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('source_id');
  };

  const selectedSourceId = getSelectedSourceId();

  // Helper function to get wallet logo based on source_name
  const getWalletLogo = (sourceName) => {
    const walletMap = {
      'coinbase_pro': '/exchanges/coinbase_pro.png',
      'coinbase': '/exchanges/coinbase.png',
      'binance': '/exchanges/binance.png',
      'metamask': '/exchanges/metamask.png',
      'bitcoin': '/exchanges/bitcoin.png',
      'ethereum': '/exchanges/ethereum.png'
    };
    return walletMap[sourceName] || '/exchanges/default.png';
  };

  // Filter sources based on search term (search by display_name)
  const filteredSources = sources.filter(source => 
    source.display_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handle source selection
  const handleSourceClick = (source) => {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('source_id', source.id);
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.pushState({}, '', newUrl);
    window.location.reload();
  };

  return (
    <Box
      sx={{
        width: 280,
        height: 'calc(100vh - 64px)', // Account for navbar height
        marginTop: '64px', // Start below navbar
        background: '#3b82f6', // Match navbar color
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 1200, // Lower than navbar (1300)
        overflow: 'hidden'
      }}
    >
      {/* Search Field */}
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search wallet"
          value={searchTerm}
          onChange={handleSearchChange}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
              </InputAdornment>
            ),
            sx: {
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderRadius: 1,
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '& input': {
                color: 'white',
                '&::placeholder': {
                  color: 'rgba(255,255,255,0.7)'
                }
              }
            }
          }}
        />
      </Box>

      {/* Select Wallet Section */}
      <Box sx={{ px: 2, pb: 2 }}>
        <ListItemButton
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            borderRadius: 2,
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.05)'
            },
            py: 1.5
          }}
        >
          <ListItemIcon>
            <AccountBalanceWalletIcon sx={{ color: 'white' }} />
          </ListItemIcon>
          <ListItemText
            primary="Sources"
            primaryTypographyProps={{
              fontWeight: 500,
              fontSize: '1.1rem'
            }}
          />
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ListItemButton>
      </Box>

      {/* Wallets List */}
      <Collapse in={isExpanded} sx={{ flex: 1, overflow: 'auto' }}>
        <List sx={{ px: 2, py: 0 }}>
          {filteredSources.length > 0 ? (
            filteredSources.map((source) => {
              const isSelected = selectedSourceId && parseInt(selectedSourceId) === source.id;
              return (
                <ListItem key={source.id} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    sx={{
                      borderRadius: 2,
                      backgroundColor: isSelected ? 'rgba(255,255,255,0.15)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.1)'
                      },
                      py: 2,
                      px: 2,
                      minHeight: 60
                    }}
                    onClick={() => handleSourceClick(source)}
                  >
                    <ListItemIcon sx={{ minWidth: 40, mr: 2 }}>
                      <Box
                        component="img"
                        src={getWalletLogo(source.source_name)}
                        alt={source.display_name}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 2
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={source.display_name}
                      primaryTypographyProps={{
                        color: 'white',
                        fontWeight: isSelected ? 600 : 400,
                        fontSize: '1rem'
                      }}
                      sx={{ flex: 1 }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })
          ) : (
            <ListItem>
              <ListItemText
                primary="No sources configured"
                primaryTypographyProps={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '0.9rem',
                  textAlign: 'center'
                }}
              />
            </ListItem>
          )}
        </List>
      </Collapse>
    </Box>
  );
};

WalletsSidebar.propTypes = {
  sources: PropTypes.array.isRequired
};

WalletsSidebar.defaultProps = {
  sources: []
};

export default WalletsSidebar; 
